import { useState } from "react";
import { ArrowUpRight, BedDouble, CheckCircle2, Maximize, Users, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Eyebrow } from "./Reveal";
import { useSite, bookingUrl } from "@/context/SiteContext";

const AvailabilityCheck = ({ roomId, checkin, nights, onCheckin, onNights }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    if (!checkin) {
      setState({ error: true });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get("/availability", { params: { room_id: roomId, checkin, nights } });
      setState(data);
    } catch {
      setState({ error: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 rounded-2xl bg-sand2/70 border border-ink/10 p-3.5" data-testid={`availability-widget-${roomId}`}>
      <div className="flex flex-wrap items-end gap-2.5">
        <input
          type="date"
          value={checkin}
          onChange={(e) => { onCheckin(e.target.value); setState(null); }}
          data-testid={`availability-checkin-${roomId}`}
          className="rounded-lg border border-ink/15 bg-white px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-azure/40"
        />
        <select
          value={nights}
          onChange={(e) => onNights(e.target.value)}
          data-testid={`availability-nights-${roomId}`}
          className="rounded-lg border border-ink/15 bg-white px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-azure/40"
        >
          {Array.from({ length: 14 }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>{n} night{n > 1 ? "s" : ""}</option>
          ))}
        </select>
        <button
          onClick={check}
          disabled={busy}
          data-testid={`availability-check-${roomId}`}
          className="rounded-lg bg-ink text-sand text-xs font-medium px-4 py-2 hover:bg-azure transition-colors disabled:opacity-60"
        >
          {busy ? "…" : "Check dates"}
        </button>
        {state && !state.error && (
          <span
            className={`inline-flex items-center gap-1.5 text-[11px] font-mono ${state.available ? "text-azure" : "text-coral"}`}
            data-testid={`availability-status-${roomId}`}
          >
            {state.available ? <><CheckCircle2 size={13} /> Available</> : <><XCircle size={13} /> Booked — try other dates</>}
          </span>
        )}
      </div>
    </div>
  );
};

const RoomRow = ({ room, index }) => {
  const { settings } = useSite();
  const [checkin, setCheckin] = useState("");
  const [nights, setNights] = useState("2");
  const flip = index % 2 === 1;

  return (
    <Reveal data-testid={`room-row-${room.id}`}>
      <article className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-center" data-testid={`room-card-${room.id}`}>
        <div className={`lg:col-span-7 relative ${flip ? "lg:order-2" : ""}`}>
          <div className="relative rounded-[2rem] overflow-hidden group shadow-[0_24px_70px_rgba(14,27,46,0.14)]">
            <img
              src={room.images?.[0]}
              alt={room.image_alts?.[0] || `${room.name} at Mahadinusa Lembongan`}
              className="w-full h-72 sm:h-[420px] object-cover transition-transform duration-[1.2s] group-hover:scale-105"
            />
            <span className="absolute top-5 left-5 bg-ink/80 backdrop-blur text-sand font-serif text-2xl h-12 w-12 grid place-items-center rounded-full">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="absolute bottom-5 left-5 bg-sand/90 backdrop-blur text-ink text-xs font-medium px-4 py-2 rounded-full" data-testid={`room-price-${room.id}`}>
              Start from {room.price_label}
            </span>
          </div>
          {room.images?.[1] && (
            <img
              src={room.images[1]}
              alt={room.image_alts?.[1] || `${room.name} — detail at Mahadinusa Lembongan`}
              className={`hidden sm:block absolute -bottom-8 w-40 h-28 object-cover rounded-2xl border-4 border-sand shadow-[0_16px_40px_rgba(14,27,46,0.2)] ${flip ? "left-8" : "right-8"}`}
            />
          )}
        </div>

        <div className={`lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-azure">{room.tagline}</p>
          <h3 className="mt-3 font-serif text-2xl sm:text-3xl tracking-tight">{room.name}</h3>
          <p className="mt-4 text-sm sm:text-base text-seastone leading-relaxed">{room.description}</p>

          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-seastone">
            {room.size && <span className="inline-flex items-center gap-1.5"><Maximize size={12} /> {room.size}</span>}
            {room.sleeps && <span className="inline-flex items-center gap-1.5"><Users size={12} /> {room.sleeps}</span>}
          </div>

          {room.features?.length > 0 && (
            <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5">
              {room.features.slice(0, 6).map((f) => (
                <li key={f} className="flex items-center gap-2 text-xs text-ink/75">
                  <BedDouble size={12} className="text-gold shrink-0" /> {f}
                </li>
              ))}
            </ul>
          )}

          <AvailabilityCheck roomId={room.id} checkin={checkin} nights={nights} onCheckin={setCheckin} onNights={setNights} />

          <div className="mt-5 flex items-center gap-4">
            <a
              href={bookingUrl(settings, { checkin, nights })}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`reserve-now-room-${room.id}`}
              className="inline-flex items-center gap-2 rounded-full bg-ink text-sand text-sm font-medium px-6 py-3 transition-all hover:bg-azure hover:shadow-[0_0_0_5px_rgba(26,98,137,0.15)]"
            >
              Reserve Now <ArrowUpRight size={15} />
            </a>
          </div>
        </div>
      </article>
    </Reveal>
  );
};

export const Rooms = () => {
  const { rooms } = useSite();

  return (
    <section id="rooms" data-testid="rooms-section" className="py-20 sm:py-28 bg-sand2/60 border-y border-ink/10 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div>
            <Eyebrow>Stay</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-balance">
              Choose your corner of the island
            </h2>
          </div>
          <p className="max-w-sm text-sm text-seastone leading-relaxed">
            Every stay includes the flowering pool, the garden and our island host a message away.
          </p>
        </Reveal>

        <div className="space-y-16 sm:space-y-24">
          {rooms.map((room, i) => (
            <RoomRow key={room.id} room={room} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
